/**
 * Manchester Triage System - Core Engine
 * 
 * This is the main file for the Manchester Triage System implementation.
 * It provides functions to evaluate patient data against MTS rules and
 * generate detailed triage explanations for the Glass Box AI approach.
 * 
 * Reference: Manchester Triage Group. Emergency Triage: Manchester Triage Group.
 * 3rd ed. Wiley-Blackwell; 2014.
 */

import { 
  TriageData, 
  TriageExplanation, 
  ExplanationFactor,
  UrgencyLevel,
  MTSPresentation,
  MTSDiscriminator,
  SymptomWithSource,
  RuleCondition
} from '@/types/triage';
import { MTS_PRESENTATIONS, findPresentationByName, getApplicablePresentations } from './presentations';
import { MTS_DISCRIMINATORS, findDiscriminatorByName } from './discriminators';
import { MTS_RULES, evaluateRule, normalizeTemperature } from './rules';

/**
 * Map of urgency levels to numerical values for comparison
 */
const URGENCY_VALUES: Record<UrgencyLevel, number> = {
  'immediate': 4,
  'urgent': 3,
  'standard': 2,
  'non-urgent': 1
};

/**
 * Helper function to safely get the primary condition from a rule
 * Works with both single conditions and compound rules (arrays of conditions)
 */
function getPrimaryCondition(condition: RuleCondition | RuleCondition[]): RuleCondition {
  if (Array.isArray(condition)) {
    // For compound rules, return the first condition as the primary one for display
    return condition[0];
  }
  return condition;
}

/**
 * Helper function to safely get parameter from a condition
 */
function getParameter(condition: RuleCondition | RuleCondition[]): string {
  return getPrimaryCondition(condition).parameter;
}

/**
 * Helper function to safely get comparator from a condition
 */
function getComparator(condition: RuleCondition | RuleCondition[]): string {
  return getPrimaryCondition(condition).comparator;
}

/**
 * Helper function to safely get value from a condition
 */
function getValue(condition: RuleCondition | RuleCondition[]): any {
  return getPrimaryCondition(condition).value;
}

/**
 * Extract symptoms from additionalInfo free text and add them to the symptoms array
 * This helps ensure symptoms mentioned in free text are properly considered in triage
 * @returns An object containing the processed triage data and symptoms with their sources
 */
export function extractSymptomsFromAdditionalInfo(triageData: TriageData): { 
  processedData: TriageData; 
  symptomsWithSources: SymptomWithSource[]; 
} {
  if (!triageData.additionalInfo || typeof triageData.additionalInfo !== 'string' || triageData.additionalInfo.trim() === '') {
    // Return original data with source tracking for existing symptoms
    return {
      processedData: triageData,
      symptomsWithSources: (triageData.symptoms || []).map(symptom => ({
        name: symptom,
        source: 'explicit'
      }))
    };
  }

  // Make a copy of the data to avoid mutating the original
  const processedData = { ...triageData };
  if (!processedData.symptoms) {
    processedData.symptoms = [];
  }
  
  // Track symptoms with their sources
  const symptomsWithSources: SymptomWithSource[] = [];
  
  // First, add all explicit symptoms with their source
  processedData.symptoms.forEach(symptom => {
    symptomsWithSources.push({
      name: symptom,
      source: 'explicit'
    });
  });

  // Common symptoms to look for in free text
  const commonSymptoms = [
    { keyword: "headache", symptom: "Headache" },
    { keyword: "nausea", symptom: "Nausea" },
    { keyword: "vomiting", symptom: "Vomiting" },
    { keyword: "dizziness", symptom: "Dizziness" },
    { keyword: "fever", symptom: "Fever" },
    { keyword: "cough", symptom: "Cough" },
    { keyword: "chest pain", symptom: "Chest pain" },
    { keyword: "shortness of breath", symptom: "Shortness of breath" },
    { keyword: "difficulty breathing", symptom: "Shortness of breath" },
    { keyword: "abdominal pain", symptom: "Abdominal pain" },
    { keyword: "stomach pain", symptom: "Abdominal pain" },
    { keyword: "diarrhea", symptom: "Diarrhea" },
    { keyword: "fatigue", symptom: "Fatigue" },
    { keyword: "weakness", symptom: "Fatigue" },
    { keyword: "sore throat", symptom: "Sore throat" },
    { keyword: "rash", symptom: "Rash" },
    { keyword: "pain", symptom: "Pain" }
  ];

  const normalizedText = triageData.additionalInfo.toLowerCase();
  // console.log(`[MTSEngine Debug] Extracting symptoms from additionalInfo: "${normalizedText}"`);

  const specificPainSymptomNames = ["Chest pain", "Abdominal pain"]; // List of SYMPTOM NAMES that are specific pains

  // Check for each symptom in the text
  commonSymptoms.forEach(({ keyword, symptom }) => {
    if (normalizedText.includes(keyword)) { // If the keyword is found in the text
      if (symptom === "Pain") {
        // Current symptom being considered is the generic "Pain"
        const isSpecificPainIndicated = specificPainSymptomNames.some(specificSymptomName =>
          processedData.symptoms.includes(specificSymptomName) || // Already present from input or previous iterations
          commonSymptoms.some(cs => // Or its keyword is found, and it maps to this specific pain symptom name
            cs.symptom === specificSymptomName && normalizedText.includes(cs.keyword)
          )
        );

        if (!isSpecificPainIndicated && !processedData.symptoms.includes(symptom)) {
          // If no specific pain is indicated AND generic "Pain" isn't already there, add generic "Pain"
          processedData.symptoms.push(symptom);
          symptomsWithSources.push({
            name: symptom,
            source: 'extracted'
          });
          // console.log(`[MTSEngine Debug] Extracted symptom from additionalInfo: "${symptom}" (Generic Pain Added)`);
        }
      } else {
        // For all other symptoms (including specific pains like "Chest pain", "Abdominal pain")
        if (!processedData.symptoms.includes(symptom)) { // If not already present
          processedData.symptoms.push(symptom);
          symptomsWithSources.push({
            name: symptom,
            source: 'extracted'
          });
          // console.log(`[MTSEngine Debug] Extracted symptom from additionalInfo: "${symptom}"`);
        }
      }
    }
  });

  return {
    processedData,
    symptomsWithSources
  };
}

/**
 * Main function to evaluate patient data against MTS rules and
 * generate a comprehensive triage explanation
 */
export function explainTriage(triageData: TriageData): TriageExplanation {
  console.log(`[MTSEngine Debug] explainTriage called. Temp: ${triageData.vitals?.temperature}, RespDistress: ${triageData.respiratory_distress}`);
  console.log('[MTSEngine Debug] TriageData:', JSON.stringify(triageData, null, 2).substring(0, 500));
  try {
    // Preprocess the triage data for better rule matching
    let processedTriageData = { ...triageData };
    let symptomsWithSources: SymptomWithSource[] = [];
    
    // Extract symptoms from additionalInfo field
    const extractionResult = extractSymptomsFromAdditionalInfo(processedTriageData);
    processedTriageData = extractionResult.processedData;
    symptomsWithSources = extractionResult.symptomsWithSources;
    
    // Ensure symptoms array exists and is normalized
    if (!processedTriageData.symptoms) {
      processedTriageData.symptoms = [];
    }
    
    // Normalize temperature if it exists
    if (processedTriageData.vitals?.temperature) {
      // Store both the original and normalized temperature for rule matching
      const originalTemp = processedTriageData.vitals.temperature;
      const normalizedTemp = normalizeTemperature(originalTemp);
      
      // If the temperature was in Fahrenheit, store the normalized value
      if (originalTemp > 45) {
        processedTriageData.vitals.temperature = normalizedTemp;
        console.log(`[MTSEngine Debug] Normalized temperature from ${originalTemp}°F to ${normalizedTemp.toFixed(1)}°C for rule matching`);
      }
    }
    
    // Find all rules that match the patient's data
    // Check each rule individually for better debugging
    const matchedRules = [];
    for (const rule of MTS_RULES) {
      try {
        const matches = evaluateRule(rule, processedTriageData);
        if (matches) {
          matchedRules.push(rule);
          console.log(`[MTSEngine Debug] Rule matched: ${rule.id} - ${rule.name}`);
        }
      } catch (ruleError) {
        console.error(`[MTSEngine Debug] Error evaluating rule ${rule.id}:`, ruleError);
      }
    }
    
    console.log(`[MTSEngine Debug] Matched rules count: ${matchedRules.length}. IDs: ${matchedRules.map(r => r.id).join(', ')}`);
    
    // Find the highest urgency level from matched rules
    let highestUrgency: UrgencyLevel = 'non-urgent';
    let urgencyValue = 0;
    
    matchedRules.forEach(rule => {
      const currentUrgencyValue = URGENCY_VALUES[rule.impact.urgency] || 0;
      if (currentUrgencyValue > urgencyValue) {
        urgencyValue = currentUrgencyValue;
        highestUrgency = rule.impact.urgency;
      }
    });
    
    // Determine the presenting complaint based on symptoms
    const presentingComplaint = determinePresentation(processedTriageData, matchedRules);
//     console.log(`[MTSEngine Debug] Determined presentingComplaint: ${presentingComplaint}`);
    
    // Determine the discriminator that led to the assigned urgency
    const discriminator = determineDiscriminator(matchedRules, highestUrgency);
//     console.log(`[MTSEngine Debug] Determined discriminator: ${discriminator} for urgency: ${highestUrgency}`);
    
    // Generate explanation factors for transparency
    let factors = generateExplanationFactors(matchedRules, processedTriageData);
    
    // Format temperature values in the explanation factors
    factors = factors.map(factor => {
      // Check if this factor contains temperature information
      if (factor.parameter.includes('Temperature') || 
          factor.description.toLowerCase().includes('temperature') || 
          factor.description.toLowerCase().includes('fever')) {
        
        // Format the temperature value if it's a number
        let formattedDescription = factor.description;
        let formattedValue = factor.value;
        
        // Format the value if it's a temperature with unit
        if (typeof factor.value === 'string' && factor.value.includes('°')) {
          const tempMatch = factor.value.match(/(\d+(?:\.\d+)?)°([CF])/i);
          if (tempMatch) {
            const tempValue = parseFloat(tempMatch[1]);
            const unit = tempMatch[2].toUpperCase();
            formattedValue = `${tempValue.toFixed(1)}°${unit}`;
          }
        }
        
        // Find and format temperatures in the description
        const tempRegex = /(\d+(?:\.\d+)?)°([CF])/gi;
        formattedDescription = formattedDescription.replace(tempRegex, (match, temp, unit) => {
          return `${parseFloat(temp).toFixed(1)}°${unit.toUpperCase()}`;
        });
        
        return {
          ...factor,
          value: formattedValue,
          description: formattedDescription
        };
      }
      
      return factor;
    });
    
    // Calculate confidence in the triage decision
//     console.log(`[MTSEngine Debug] About to call local calculateConfidence. MatchedRules: ${matchedRules.length}, Urgency: ${highestUrgency}`);
    const confidence = calculateConfidence(matchedRules, triageData, highestUrgency);
//     console.log(`[MTSEngine Debug] Local calculateConfidence returned: ${confidence.toFixed(3)}`);
    
    // Return the comprehensive triage explanation with processed symptoms and their sources
    return {
      urgency: highestUrgency,
      confidence,
      factors,
      rules: matchedRules.map(r => r.id),
      presentingComplaint,
      discriminator,
      processedSymptoms: processedTriageData.symptoms,
      symptomsWithSources: symptomsWithSources
    };
  } catch (error) {
    const err = error as Error;
    console.error('[MTSEngine Debug] Error in explainTriage:', err.message, err.stack ? '\nStack: ' + err.stack.substring(0, 700) : 'No stack trace available.');
    
    // Extract the primary symptom for better presentation
    const primarySymptom = triageData.symptoms && triageData.symptoms.length > 0 ? 
      triageData.symptoms[0] : 'Unspecified symptoms';
    
    // Determine a reasonable urgency based on available data
    let fallbackUrgency: UrgencyLevel = 'standard'; // Default to standard as safer than non-urgent
    let fallbackConfidence = 0.4; // Low but not very low confidence
    
    // Check for any critical values that might suggest higher urgency
    if (triageData.respiratory_distress) {
      fallbackUrgency = 'urgent';
      fallbackConfidence = 0.6;
    } else if (triageData.vitals?.temperature && triageData.vitals.temperature > 38.5) {
      fallbackUrgency = 'urgent';
      fallbackConfidence = 0.5;
    }
    
    // Improved fallback explanation when an error occurs
    return {
      urgency: fallbackUrgency,
      confidence: fallbackConfidence,
      factors: [{
        parameter: 'clinical assessment',
        value: 'limited data',
        impact: 'medium',
        description: 'Assessment based on limited clinical data. Consider manual review.'
      }],
      rules: [],
      presentingComplaint: primarySymptom,
      discriminator: 'Clinical Assessment',
      processedSymptoms: triageData.symptoms || [],
      symptomsWithSources: (triageData.symptoms || []).map(symptom => ({
        name: symptom,
        source: 'explicit' // In error case, assume all symptoms were explicitly provided
      }))
    };
  }
}

/**
 * Determine the most likely presenting complaint based on
 * matched rules and patient data
 */
function determinePresentation(
  triageData: TriageData, 
  matchedRules: typeof MTS_RULES
): string | undefined {
  // First try to get presenting complaint from highest urgency matched rule
  let highestUrgencyValue = 0;
  let presentingComplaint: string | undefined;
  
  matchedRules.forEach(rule => {
    const urgencyValue = URGENCY_VALUES[rule.impact.urgency] || 0;
    if (urgencyValue > highestUrgencyValue && rule.presentingComplaint) {
      highestUrgencyValue = urgencyValue;
      presentingComplaint = rule.presentingComplaint;
    }
  });
  
  if (presentingComplaint) return presentingComplaint;
  
  // If no presenting complaint found in rules, try to determine from symptoms
  if (triageData.symptoms?.length > 0) {
    // Check for common symptoms and map to presentations
    if (triageData.symptoms.some(s => 
      s.toLowerCase().includes('breath') || 
      s.toLowerCase().includes('dyspnea')
    )) {
      return triageData.age && triageData.age < 16 
        ? 'Shortness of Breath in Children' 
        : 'Shortness of Breath in Adults';
    }
    
    if (triageData.symptoms.some(s => 
      s.toLowerCase().includes('abdominal pain') || 
      s.toLowerCase().includes('stomach pain')
    )) {
      return triageData.age && triageData.age < 16 
        ? 'Abdominal Pain in Children' 
        : 'Abdominal Pain in Adults';
    }
    
    if (triageData.symptoms.some(s => s.toLowerCase().includes('chest pain'))) {
      return 'Chest Pain';
    }
    
    if (triageData.symptoms.some(s => s.toLowerCase().includes('headache'))) {
      return 'Headache';
    }
  }
  
  // Default return if no clear presentation can be determined
  return undefined;
}

/**
 * Determine the discriminator that led to the assigned urgency
 */
function determineDiscriminator(
  matchedRules: typeof MTS_RULES, 
  assignedUrgency: UrgencyLevel
): string | undefined {
  // Filter rules that match the assigned urgency
  const urgencyRules = matchedRules.filter(rule => 
    rule.impact.urgency === assignedUrgency
  );
  
  // Return the discriminator from the first matching rule
  if (urgencyRules.length > 0 && urgencyRules[0].discriminator) {
    return urgencyRules[0].discriminator;
  }
  
  return undefined;
}

/**
 * Generate explanation factors for transparency in the triage decision
 */
function generateExplanationFactors(
  matchedRules: typeof MTS_RULES,
  triageData: TriageData
): ExplanationFactor[] {
  const factors: ExplanationFactor[] = [];
  
  // Add factors from matched rules
  matchedRules.forEach(rule => {
    const { condition, impact } = rule;
    
    // Determine the impact level based on urgency
    let factorImpact: 'high' | 'medium' | 'low';
    switch (impact.urgency) {
      case 'immediate':
        factorImpact = 'high';
        break;
      case 'urgent':
        factorImpact = 'high';
        break;
      case 'standard':
        factorImpact = 'medium';
        break;
      default:
        factorImpact = 'low';
    }
    
    // Special handling for temperature-related rules
    if (getParameter(condition) === 'vitals.temperature' && triageData.vitals?.temperature) {
      const temp = Number(triageData.vitals.temperature);
      const isFahrenheit = temp > 45;
      const tempCelsius = isFahrenheit ? normalizeTemperature(temp) : temp;
      const displayTemp = `${temp.toFixed(1)}${isFahrenheit ? '°F' : '°C'}`;
      
      // Override the value with the properly formatted temperature
      let description = impact.description;
      
      // Update description to include the unit information
      if (isFahrenheit) {
        description += ` (${displayTemp}, equivalent to ${tempCelsius.toFixed(1)}°C)`;
      }
      
      factors.push({
        parameter: formatParameterName(getParameter(condition)),
        value: displayTemp,
        impact: factorImpact,
        description: description
      });
      
      // Skip the default factor addition for temperature rules
      return;
    }
    
    // Extract the value that matched the condition
    let parameterValue: any;
    
    if (getParameter(condition).includes('.')) {
      const [parent, child] = getParameter(condition).split('.');
      const parentObj = triageData[parent as keyof TriageData];
      if (parentObj && typeof parentObj === 'object') {
        parameterValue = (parentObj as any)[child];
      }
    } else {
      parameterValue = triageData[getParameter(condition) as keyof TriageData];
    }
    
    // Format the parameter name for display
    const displayParameter = formatParameterName(getParameter(condition));
    
    // Add the factor
    factors.push({
      parameter: displayParameter,
      value: parameterValue,
      impact: factorImpact,
      description: impact.description
    });
  });
  
  // Add demographic factors if they influenced the decision
  if (triageData.age !== undefined) {
    // Check if age influenced pediatric vs adult pathway
    const isChild = triageData.age < 16;
    factors.push({
      parameter: 'Age',
      value: triageData.age,
      impact: 'medium',
      description: isChild 
        ? 'Patient is a child, pediatric triage protocols applied' 
        : 'Patient is an adult, standard triage protocols applied'
    });
  }
  
  // Add vital signs factors if available
  if (triageData.vitals) {
    // Add temperature if available
    if (triageData.vitals.temperature) {
      const temp = Number(triageData.vitals.temperature);
      const isFahrenheit = temp > 45;
      const tempCelsius = isFahrenheit ? normalizeTemperature(temp) : temp;
      const displayTemp = `${temp.toFixed(1)}${isFahrenheit ? '°F' : '°C'}`;
      
      // Generate appropriate description based on normalized temperature
      let description = '';
      let impact: 'high' | 'medium' | 'low' = 'low';
      
      if (tempCelsius > 39) {
        impact = 'high';
        description = 'Significantly elevated temperature indicates potential serious infection';
      } else if (tempCelsius > 38) {
        impact = 'medium';
        description = 'Fever requires assessment for source of infection';
      } else {
        impact = 'low';
        description = 'Temperature is within normal range';
      }
      
      // Add unit conversion information if in Fahrenheit
      if (isFahrenheit) {
        description += ` (${displayTemp}, equivalent to ${tempCelsius.toFixed(1)}°C)`;
      }
      
      factors.push({
        parameter: 'Temperature',
        value: displayTemp,
        impact: impact,
        description: description
      });
    }
  }
  
  if (triageData.vitals.heartRate) {
    const hr = parseFloat(triageData.vitals.heartRate);
    if (!isNaN(hr)) {
      if (hr > 120) {
        factors.push({
          parameter: 'Heart Rate',
          value: triageData.vitals.heartRate,
          impact: 'high',
          description: 'Significantly elevated heart rate indicates potential serious condition'
        });
      } else if (hr > 100) {
        factors.push({
          parameter: 'Heart Rate',
          value: triageData.vitals.heartRate,
          impact: 'medium',
          description: 'Elevated heart rate may indicate pain, anxiety, or medical condition'
        });
      }
    }
  }
  
  // Sort factors by impact level (high to low)
  return factors.sort((a, b) => {
    const impactValues = { high: 3, medium: 2, low: 1 };
    return impactValues[b.impact] - impactValues[a.impact];
  });
}

/**
 * Format parameter names for more readable display
 */
function formatParameterName(parameter: string): string {
  if (parameter.includes('.')) {
    const [parent, child] = parameter.split('.');
    const formattedParent = parent.charAt(0).toUpperCase() + parent.slice(1);
    const formattedChild = child.charAt(0).toUpperCase() + child.slice(1);
    return `${formattedParent} - ${formattedChild}`;
  }
  
  return parameter.charAt(0).toUpperCase() + parameter.slice(1)
    .replace(/([A-Z])/g, ' $1')
    .replace(/_/g, ' ');
}

/**
 * Calculate confidence score for the triage decision
 */
function calculateConfidence(
  matchedRules: typeof MTS_RULES,
  triageData: TriageData,
  assignedUrgency: UrgencyLevel
): number {
//   console.log(`[MTSEngine Debug] calculateConfidence START. MatchedRules: ${matchedRules.length}, AssignedUrgency: ${assignedUrgency}`);
  // Base confidence starts at 0.7 (reasonable default)
  let confidence = 0.7;
//     console.log(`[MTSEngine Debug] Confidence base: ${confidence.toFixed(3)}`);
  
  // More matched rules increase confidence
  if (matchedRules.length > 3) {
    confidence += 0.1;
//       console.log(`[MTSEngine Debug] Confidence after matchedRules > 3 bonus (+0.1): ${confidence.toFixed(3)}`);
  } else if (matchedRules.length <= 1) {
    confidence -= 0.1;
//       console.log(`[MTSEngine Debug] Confidence after matchedRules <= 1 penalty (-0.1): ${confidence.toFixed(3)}`);
  }
  
  // Rules matching the assigned urgency increase confidence
  const urgencyMatchCount = matchedRules.filter(
    rule => rule.impact.urgency === assignedUrgency
  ).length;
//     console.log(`[MTSEngine Debug] UrgencyMatchCount (rules matching assignedUrgency '${assignedUrgency}'): ${urgencyMatchCount}`);
  if (urgencyMatchCount > 1) {
    confidence += 0.1;
//       console.log(`[MTSEngine Debug] Confidence after urgencyMatchCount > 1 bonus (+0.1): ${confidence.toFixed(3)}`);
  }
  
  // More complete patient data increases confidence
  const dataCompleteness = calculateDataCompleteness(triageData);
//     console.log(`[MTSEngine Debug] Data Completeness score from calculateDataCompleteness: ${dataCompleteness.toFixed(3)}`);
  if (dataCompleteness > 0.8) {
    confidence += 0.1;
//       console.log(`[MTSEngine Debug] Confidence after dataCompleteness > 0.8 bonus (+0.1): ${confidence.toFixed(3)}`);
  } else if (dataCompleteness < 0.5) {
    confidence -= 0.2;
//       console.log(`[MTSEngine Debug] Confidence after dataCompleteness < 0.5 penalty (-0.2): ${confidence.toFixed(3)}`);
  }
  
  // Check for temperature unit mismatches
  if (triageData.vitals?.temperature && triageData.symptoms?.includes('fever')) {
//     console.log(`[MTSEngine Debug] Checking temp mismatch. Temp: ${triageData.vitals.temperature}, Symptoms include 'fever'.`);
    const temp = Number(triageData.vitals.temperature);
    const isFahrenheit = temp > 45;
    const tempCelsius = isFahrenheit ? normalizeTemperature(temp) : temp;
//       console.log(`[MTSEngine Debug] Temp details - Original: ${triageData.vitals.temperature}, IsFahrenheit: ${isFahrenheit}, CelsiusEquiv: ${tempCelsius.toFixed(2)}`);
    
    // If temperature is normal but fever is reported, reduce confidence
    if (tempCelsius < 37.5) {
      confidence -= 0.2;
//         console.log(`[MTSEngine Debug] Confidence after temp mismatch (normal temp ${tempCelsius.toFixed(2)}C with 'fever' symptom) penalty (-0.2): ${confidence.toFixed(3)}`);
    }
  }
  
  const finalConfidence = Math.min(0.95, Math.max(0.3, confidence));
//   console.log(`[MTSEngine Debug] calculateConfidence END. Final confidence before cap: ${confidence.toFixed(3)}, After cap [0.3, 0.95]: ${finalConfidence.toFixed(3)}`);
  return finalConfidence;
}

/**
 * Calculate completeness of patient data
 */
function calculateDataCompleteness(triageData: TriageData): number {
//   console.log('[MTSEngine Debug] calculateDataCompleteness START.');
  let totalFields = 0;
  let populatedFields = 0;
  
  // Count base fields
  const baseFields: (keyof TriageData)[] = [
    'symptoms', 'severity', 'duration', 'onset', 'additionalInfo',
    'medicalHistory', 'medications', 'allergies'
  ];
  
  totalFields += baseFields.length;
  baseFields.forEach(field => {
    const value = triageData[field];
    const isPopulated = value !== undefined && 
                        value !== null && 
                        (!Array.isArray(value) || (value as any[]).length > 0) &&
                        (typeof value !== 'string' || (value as string).trim() !== '');
    if (isPopulated) populatedFields++;
//     console.log(`[MTSEngine Debug] Completeness - Base field '${field}': ${isPopulated ? 'Populated' : 'Not Populated'} (Value: ${JSON.stringify(value)})`);
  });
  
  // Count vital sign fields
  const vitalFields = ['temperature', 'heartRate', 'bloodPressure', 'respiratoryRate', 'oxygenSaturation'];
  totalFields += vitalFields.length;
  vitalFields.forEach(field => {
    const value = triageData.vitals?.[field as keyof typeof triageData.vitals];
    const isPopulated = !!value;
    if (isPopulated) populatedFields++;
//     console.log(`[MTSEngine Debug] Completeness - Vital field '${field}': ${isPopulated ? 'Populated' : 'Not Populated'} (Value: ${JSON.stringify(value)})`);
  });
  
  // Count demographic fields
  const demoFields: (keyof TriageData)[] = ['age', 'gender'];
  totalFields += demoFields.length;
  demoFields.forEach(field => {
    const value = triageData[field];
    const isPopulated = value !== undefined && value !== null;
    if (isPopulated) populatedFields++;
//     console.log(`[MTSEngine Debug] Completeness - Demo field '${field}': ${isPopulated ? 'Populated' : 'Not Populated'} (Value: ${JSON.stringify(value)})`);
  });
  
  const completenessScore = totalFields > 0 ? populatedFields / totalFields : 0;
//   console.log(`[MTSEngine Debug] calculateDataCompleteness END. Populated: ${populatedFields}, Total: ${totalFields}, Score: ${completenessScore.toFixed(3)}`);
  return completenessScore;
}

/**
 * Connect MTS presentations with their discriminators
 * This function mutates the MTS_PRESENTATIONS array by linking
 * each presentation with its relevant discriminators
 */
export function linkPresentationsWithDiscriminators(): void {
  // Map of presentation names to common discriminators
  const commonDiscriminatorMap: Record<string, string[]> = {
    // Examples of presentation to discriminator mappings
    'Shortness of Breath in Adults': [
      'Airway Compromise', 'Inadequate Breathing', 'Stridor', 
      'Respiratory Exhaustion', 'Inability to Talk in Sentences', 'Audible Wheeze'
    ],
    'Chest Pain': [
      'Shock', 'Chest Pain', 'Severe Pain', 'Moderate Pain'
    ],
    'Abdominal Pain in Adults': [
      'Shock', 'Severe Pain', 'Moderate Pain', 'Recent Problem', 'Low Pain'
    ],
    'Collapsed Adult': [
      'Unresponsive', 'Altered Level of Consciousness', 'History of Unconsciousness'
    ],
    'Head Injury': [
      'Unresponsive', 'Altered Level of Consciousness', 'Severe Mechanism of Injury'
    ],
    'Major Trauma': [
      'Airway Compromise', 'Inadequate Breathing', 'Shock', 'Exsanguinating Hemorrhage',
      'Major Trauma', 'Severe Mechanism of Injury', 'High-Energy Transfer'
    ]
    // Additional mappings would be added for all presentations
  };
  
  // For each presentation, link its discriminators
  MTS_PRESENTATIONS.forEach(presentation => {
    const discriminatorNames = commonDiscriminatorMap[presentation.name] || [];
    
    // Find and link the discriminator objects
    presentation.discriminators = discriminatorNames
      .map(name => findDiscriminatorByName(name))
      .filter((d): d is MTSDiscriminator => d !== undefined);
  });
}

// Initialize the system by linking presentations with discriminators
linkPresentationsWithDiscriminators();

// Export all components for use in the application
export * from './presentations';
export * from './discriminators';
export * from './rules';

// Re-export the MTS objects for easy access
export { MTS_PRESENTATIONS, MTS_DISCRIMINATORS, MTS_RULES };
